import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Trophy, 
  Calendar, 
  Target,
  Search,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  CheckCircle2,
  XCircle,
  Loader2,
  Shield,
  TrendingUp,
  Award,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = 'info@jaydefenceacademy.com';

const AdminDashboard = () => {
  const { user, loading: authLoading, getStoredToken } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = getStoredToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    // Check if user is admin
    const userEmail = user?.email?.toLowerCase();
    const isAdmin = userEmail === ADMIN_EMAIL.toLowerCase();
    
    if (!user) {
      // No user - redirect to login
      if (!authChecked) {
        setAuthChecked(true);
        navigate('/admin/login', { replace: true });
      }
    } else if (!isAdmin) {
      // User exists but not admin
      if (!authChecked) {
        setAuthChecked(true);
        toast.error('Admin access required');
        navigate('/admin/login', { replace: true });
      }
    } else {
      // User is admin - fetch data
      setAuthChecked(true);
      fetchAdminData();
    }
  }, [user, authLoading]);

  const fetchAdminData = async () => {
    const authHeaders = getAuthHeaders();
    
    try {
      const [usersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users`, { 
          credentials: 'include',
          headers: authHeaders
        }),
        fetch(`${API_URL}/api/admin/stats`, { 
          credentials: 'include',
          headers: authHeaders
        })
      ]);

      if (usersRes.status === 403 || statsRes.status === 403 || usersRes.status === 401 || statsRes.status === 401) {
        // Don't show toast or redirect if we're already in the process of authenticating
        if (!authChecked) {
          toast.error('Admin access required');
          navigate('/admin/login', { replace: true });
        }
        return;
      }

      const usersData = await usersRes.json();
      const statsData = await statsRes.json();

      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm)
  );

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-cream-50 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-navy-900 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-navy-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-gold-400" />
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <p className="text-gray-400">Manage users, view habit tracking data, and monitor quiz scores</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-navy-900 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.total_users}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.recent_signups_7_days}</p>
                <p className="text-xs text-gray-500">New (7 days)</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <Mail className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.users_with_marketing_consent}</p>
                <p className="text-xs text-gray-500">Marketing OK</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <Award className="w-6 h-6 text-gold-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.users_with_discount}</p>
                <p className="text-xs text-gray-500">Discount Earned</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <Calendar className="w-6 h-6 text-olive-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.total_habit_logs}</p>
                <p className="text-xs text-gray-500">Habit Logs</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.total_perfect_days}</p>
                <p className="text-xs text-gray-500">Perfect Days</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{stats.total_quiz_attempts}</p>
                <p className="text-xs text-gray-500">Quiz Attempts</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="text-xl text-navy-900">User Management</CardTitle>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-cream-50">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-navy-900">User</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-navy-900">Contact</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-navy-900">Points</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-navy-900">Streak</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-navy-900">Perfect Days</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-navy-900">Quizzes</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-navy-900">Consent</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-navy-900">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <React.Fragment key={u.user_id}>
                      <tr className="border-b hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center text-white font-bold">
                              {u.name?.[0] || 'U'}
                            </div>
                            <div>
                              <p className="font-medium text-navy-900">{u.name}</p>
                              <p className="text-xs text-gray-500">{new Date(u.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-sm text-gray-700">{u.email}</p>
                          <p className="text-xs text-gray-500">{u.phone || 'No phone'}</p>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                            u.discount_earned ? 'bg-gold-100 text-gold-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            <Trophy className="w-3 h-3" />
                            {u.total_points}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="text-sm font-medium text-navy-900">
                            {u.habit_stats?.current_streak || 0} days
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="text-sm text-gray-700">
                            {u.habit_stats?.perfect_days || 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="text-sm text-gray-700">
                            {u.quiz_results?.length || 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          {u.marketing_consent ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                          >
                            {expandedUser === u.user_id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {expandedUser === u.user_id && (
                        <tr>
                          <td colSpan="8" className="bg-cream-50 p-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Quiz Results */}
                              <div>
                                <h4 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  Quiz Results
                                </h4>
                                {u.quiz_results && u.quiz_results.length > 0 ? (
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {u.quiz_results.map((quiz, idx) => (
                                      <div key={idx} className="bg-white rounded-lg p-3 border">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-sm capitalize">{quiz.exam_type}</span>
                                          <span className={`text-sm font-bold ${
                                            quiz.percentage >= 50 ? 'text-green-600' : 'text-red-600'
                                          }`}>
                                            {quiz.percentage.toFixed(1)}%
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                          {quiz.score}/{quiz.total_questions} • {new Date(quiz.created_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No quiz attempts yet</p>
                                )}
                              </div>
                              
                              {/* User Info */}
                              <div>
                                <h4 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  User Details
                                </h4>
                                <div className="bg-white rounded-lg p-4 border space-y-2">
                                  <p className="text-sm">
                                    <span className="text-gray-500">User ID:</span>{' '}
                                    <code className="text-xs bg-gray-100 px-1 rounded">{u.user_id}</code>
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-500">Email:</span> {u.email}
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-500">Phone:</span> {u.phone || 'Not provided'}
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-500">Joined:</span>{' '}
                                    {new Date(u.created_at).toLocaleString()}
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-500">Marketing:</span>{' '}
                                    {u.marketing_consent ? 'Consented' : 'Not consented'}
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-500">Discount:</span>{' '}
                                    {u.discount_earned ? (
                                      <span className="text-gold-600 font-medium">15% Earned!</span>
                                    ) : (
                                      <span>{50 - u.total_points} points to go</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              
              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No users found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
